/**
 * Toda la matemática de la escena 3D del depósito, sin React y sin Three.
 *
 * Es puro a propósito: los componentes montan lo que este módulo devuelve, así
 * que lo único que queda sin cobertura es el código que nada más dibuja. `Caja`
 * es una estructura de datos de acá, no un objeto de Three: se le pasa a un
 * `<mesh position={caja.centro} rotation={[0, caja.giroRad, 0]}>` con un
 * `<boxGeometry args={caja.tamano} />` y listo.
 *
 * ## La convención de ejes, que es el error más probable de toda la etapa
 *
 * El plano del depósito usa `x` a lo ancho y `y` **a lo largo**, con `y`
 * creciendo hacia el sur. Three usa `y` como **altura**. La conversión es:
 *
 * ```
 *   plano (x, y) + altura z   →   Three (x, z, y)
 *              ↑                            ↑  ↑
 *   lo que en el plano es `y`  ────────────────┘
 *   acá es el tercer eje (z); el segundo es la altura.
 * ```
 *
 * Por eso `cajaDesdeHuella({x, y, ancho, alto}, z0, z1)` devuelve
 * `tamano = [ancho, z1 - z0, alto]`: el `alto` de una huella del plano es su
 * medida **a lo largo**, no su altura. Está probado en `geometria.test.ts`.
 *
 * ## Coordenadas del predio
 *
 * `/mapa` entrega casi todo ya sumado, en coordenadas del predio: **acá no se
 * suma ningún origen de nave**. La excepción, y es la trampa más cara de la
 * etapa, es `racks[].geometria`, que sale **cruda, en coordenadas de la nave**
 * (`backend/src/routes/mapa.ts:303-312`). Lo único del rack que está traducido
 * al predio es `posiciones[].caja` (`mapa.ts:325`), y de ahí sale la geometría
 * de los racks: ver `huellaDeRack`.
 */

import type { AberturaMapa, Huella, Mapa, MuroMapa, NaveMapa, Punto, Rack, Sector } from '../api';

/**
 * Una caja de la escena: centro y tamaño, que es lo que Three necesita, y
 * **no** esquina y medida, que es lo que usa el resto del sistema. La
 * conversión pasa acá y en un solo lugar.
 *
 * `giroRad` es el giro alrededor del eje vertical de Three (su `y`), en
 * radianes, y va derecho a `rotation={[0, giroRad, 0]}`. Vale **0 para todo lo
 * que está alineado a los ejes** —que es casi todo— y en ese caso `centro` y
 * `tamano` son exactamente la caja envolvente, lo que le sirve a la cámara y al
 * descarte por ángulo. Existe por los **muros oblicuos**: el POST de paredes
 * sólo exige dos puntos distintos (`backend/src/routes/planta.ts:1299-1307`) y
 * `resolverMuros` emite las diagonales enteras a propósito
 * (`cerramientos.ts:236-245`). Sin giro, una pared en diagonal se dibujaría
 * como su rectángulo envolvente: una mentira visible.
 *
 * Ojo con el signo: el giro de Three es el **opuesto** al ángulo del plano,
 * porque el paso de `(x, y)` del plano a `(x, ·, z)` de Three da vuelta la
 * mano. Con `θ = atan2(dy, dx)` del plano, `giroRad = -θ`.
 */
export type Caja = {
  centro: [number, number, number];
  tamano: [number, number, number];
  giroRad: number;
};

/**
 * La misma holgura que usa el backend (`HOLGURA_M` de `cerramientos.ts`) para
 * decidir si dos medidas en metros son la misma. **Este es el único lugar donde
 * vive del lado del cliente**: `Isometrico.tsx` y `PlanoEditable.tsx` tienen hoy
 * su propia copia y tienen que importarla de acá, o la misma regla queda escrita
 * en cuatro archivos —que es justo lo que la etapa 2 acabó de arreglar para los
 * colores—.
 */
export const HOLGURA_M = 0.01;

/**
 * ¿Interrumpe la abertura el muro a nivel del piso, o hay pared abajo? Es la
 * misma pregunta que se hace `tramosMacizos` en el backend, y de su respuesta
 * depende que el tramo que llega desde `/mapa` esté partido o entero.
 */
export const llegaAlPiso = (ab: Pick<AberturaMapa, 'antepecho_m'>) => ab.antepecho_m <= HOLGURA_M;

/**
 * ¿Queda pared arriba de la abertura? Un portón de 4,5 m en un muro de 8 deja
 * un dintel de 3,5 m; un paso de piso a techo no deja nada.
 *
 * La regla del SVG (`Isometrico.tsx:239`) es esta **compuesta** con
 * `llegaAlPiso`: allá el dintel de un ventanal no hace falta porque el tramo
 * entero ya dibuja la pared de arriba y el hueco se pinta translúcido encima.
 * Acá, con cajas opacas, el dintel hace falta siempre.
 */
export const hayDintel = (ab: Pick<AberturaMapa, 'alto_m' | 'antepecho_m'>, altoMuroM: number) =>
  ab.antepecho_m + ab.alto_m < altoMuroM - HOLGURA_M;

/** Alto de una cabriada, que es lo que se ve del reticulado desde abajo. */
export const ALTO_CABRIADA_M = 0.5;
/** Espesor de una cabriada, a lo largo de la nave. */
export const ESPESOR_CABRIADA_M = 0.18;
/** Sección de un parante de rack: es un perfil, no una pared. */
export const ESPESOR_PARANTE_M = 0.09;
/** Sección de un larguero. */
export const ESPESOR_LARGUERO_M = 0.08;
/**
 * La luz que se le deja arriba a cada hueco para poder leer la pila. Es la
 * misma que usa el dibujo SVG de hoy (`Isometrico.tsx:409`): sin ella, los tres
 * niveles se ven como un bloque.
 */
export const LUZ_ENTRE_NIVELES_M = 0.06;

// ---------------------------------------------------------------------------
// De la huella del plano a la caja de Three
// ---------------------------------------------------------------------------

/**
 * Un rectángulo del plano, entre dos alturas, convertido en caja de Three.
 *
 * El tipo del rectángulo es **`Huella`** (`api.ts:507`), que es como se llama en
 * el cliente. No hay ningún `Rect` en `api.ts`; el `Rect` que existe vive en
 * `lib/camara3d.ts` y es del módulo del respaldo.
 */
export function cajaDesdeHuella(h: Huella, z0: number, z1: number): Caja {
  return {
    centro: [h.x + h.ancho / 2, (z0 + z1) / 2, h.y + h.alto / 2],
    tamano: [h.ancho, z1 - z0, h.alto],
    giroRad: 0,
  };
}

/** El largo de un segmento del plano, en metros. */
const largoDe = (a: Punto, b: Punto) => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * Un pedazo de muro —o de dintel, o de antepecho— desde su segmento en el
 * plano, su espesor y las dos alturas entre las que va.
 *
 * Los muros alineados a los ejes salen con `giroRad = 0` y el tamaño dado
 * vuelta según corresponda, así la caja es exactamente su envolvente. Sólo los
 * oblicuos giran. La cuenta del espesor a los dos lados del segmento es la
 * misma que hace `baseDelTramo` (`lib/camara3d.ts:102-115`), que está probada
 * en los cuatro sentidos y al sesgo; no se la reusa porque devuelve **cuatro
 * esquinas** y una caja de Three necesita centro, tamaño y giro, que de las
 * esquinas habría que volver a deducir. `geometria.test.ts` la usa de oráculo
 * contra el muro oblicuo, que es donde vale.
 */
function cajaDeSegmento(a: Punto, b: Punto, espesorM: number, z0: number, z1: number): Caja {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const largo = Math.hypot(dx, dy);

  // Horizontal en el plano: corre sobre x y el espesor se reparte en y.
  if (Math.abs(dy) <= HOLGURA_M) {
    return cajaDesdeHuella(
      { x: Math.min(a.x, b.x), y: (a.y + b.y) / 2 - espesorM / 2, ancho: largo, alto: espesorM },
      z0,
      z1,
    );
  }
  // Vertical en el plano: corre sobre y y el espesor se reparte en x.
  if (Math.abs(dx) <= HOLGURA_M) {
    return cajaDesdeHuella(
      { x: (a.x + b.x) / 2 - espesorM / 2, y: Math.min(a.y, b.y), ancho: espesorM, alto: largo },
      z0,
      z1,
    );
  }
  // Oblicuo: la caja va girada, con el largo sobre su eje local x.
  return {
    centro: [(a.x + b.x) / 2, (z0 + z1) / 2, (a.y + b.y) / 2],
    tamano: [largo, z1 - z0, espesorM],
    giroRad: -Math.atan2(dy, dx),
  };
}

// ---------------------------------------------------------------------------
// Muros
// ---------------------------------------------------------------------------

/** Un pedazo de muro medido sobre su propio eje, en metros desde la punta `a`. */
type Tirada = { desde: number; hasta: number };

/** A qué distancia de `a` cae el punto, proyectado sobre el muro. */
function offsetEnElMuro(muro: MuroMapa, p: Punto): number {
  const dx = muro.b.x - muro.a.x;
  const dy = muro.b.y - muro.a.y;
  const largo2 = dx * dx + dy * dy;
  if (largo2 === 0) return 0;
  return ((p.x - muro.a.x) * dx + (p.y - muro.a.y) * dy) / Math.sqrt(largo2);
}

/** El punto del muro que está a `offset` metros de la punta `a`. */
function puntoDelMuro(muro: MuroMapa, offset: number): Punto {
  const largo = largoDe(muro.a, muro.b) || 1;
  const t = offset / largo;
  return { x: muro.a.x + (muro.b.x - muro.a.x) * t, y: muro.a.y + (muro.b.y - muro.a.y) * t };
}

/** La tirada de la abertura sobre el muro, recortada a los límites del muro. */
function tiradaDeAbertura(muro: MuroMapa, ab: AberturaMapa): Tirada | null {
  const largo = largoDe(muro.a, muro.b);
  const o1 = offsetEnElMuro(muro, ab.a);
  const o2 = offsetEnElMuro(muro, ab.b);
  const desde = Math.max(0, Math.min(o1, o2));
  const hasta = Math.min(largo, Math.max(o1, o2));
  return hasta - desde > HOLGURA_M ? { desde, hasta } : null;
}

/** Lo que le queda a la tirada después de sacarle todo lo que la interrumpe. */
function restar(t: Tirada, huecos: readonly Tirada[]): Tirada[] {
  let pedazos: Tirada[] = [t];
  for (const h of huecos) {
    const siguientes: Tirada[] = [];
    for (const p of pedazos) {
      if (h.hasta <= p.desde + HOLGURA_M || h.desde >= p.hasta - HOLGURA_M) {
        siguientes.push(p);
        continue;
      }
      if (h.desde - p.desde > HOLGURA_M) siguientes.push({ desde: p.desde, hasta: h.desde });
      if (p.hasta - h.hasta > HOLGURA_M) siguientes.push({ desde: h.hasta, hasta: p.hasta });
    }
    pedazos = siguientes;
  }
  return pedazos;
}

/** La caja de un pedazo de muro dado por su tirada y sus dos alturas. */
const cajaDeTirada = (muro: MuroMapa, t: Tirada, z0: number, z1: number): Caja =>
  cajaDeSegmento(puntoDelMuro(muro, t.desde), puntoDelMuro(muro, t.hasta), muro.espesor_m, z0, z1);

/**
 * En qué cajas se dibuja un muro: los pedazos macizos, el **antepecho** de cada
 * abertura que no llega al piso y el **dintel** de cada una que no llega al
 * techo.
 *
 * El detalle que hace toda la diferencia: los `tramos` que manda `/mapa` ya
 * vienen con los huecos descontados, **pero sólo los que llegan al piso**
 * (`tramosMacizos` filtra por `llegaAlPiso`, `cerramientos.ts:155`). Un ventanal
 * con antepecho **no parte el tramo**: el tramo que llega cubre del piso al
 * techo sobre toda la pared, ventana incluida. En SVG eso alcanzaba —el hueco se
 * pinta translúcido encima y se lee como un ventanal alto, y por eso
 * `Isometrico.tsx:187-201` decide que «el antepecho no lleva caja; el dintel
 * sí»—. Con cajas **opacas** en WebGL, emitir el tramo entero entierra la
 * ventana adentro de la pared.
 *
 * Así que acá el tramo **se parte** alrededor de una abertura que no llega al
 * piso: izquierda, derecha, antepecho y dintel. Es más código que emitir el
 * tramo entero, y es la única forma de que el ventanal sea un hueco de verdad.
 * Está probado por **solape de extensiones** —no por el centro de cada caja—
 * en `geometria.test.ts`: la versión que miraba centros pasaba igual con la
 * implementación que tapa la ventana y con la que borra la pared de arriba.
 */
export function cajasDeMuro(muro: MuroMapa): Caja[] {
  const cajas: Caja[] = [];

  // Lo que parte el tramo: las aberturas que el backend **no** descontó, o sea
  // las que no llegan al piso. Las que sí llegan ya son un hueco en `tramos`.
  const partenElTramo: Tirada[] = [];
  for (const ab of muro.aberturas) {
    if (llegaAlPiso(ab)) continue;
    const t = tiradaDeAbertura(muro, ab);
    if (t) partenElTramo.push(t);
  }

  for (const tramo of muro.tramos) {
    const entero: Tirada = {
      desde: offsetEnElMuro(muro, tramo.a),
      hasta: offsetEnElMuro(muro, tramo.b),
    };
    const ordenado: Tirada =
      entero.desde <= entero.hasta ? entero : { desde: entero.hasta, hasta: entero.desde };
    for (const pedazo of restar(ordenado, partenElTramo)) {
      cajas.push(cajaDeTirada(muro, pedazo, 0, muro.alto_m));
    }
  }

  // Y se rellena lo que se sacó: la franja de abajo y la de arriba de cada
  // abertura. Como las aberturas de un muro nunca se pisan entre sí
  // (`cabeLaAbertura`), esto reconstruye exactamente lo que se quitó, sin
  // dibujar dos veces ni dejar agujeros.
  for (const ab of muro.aberturas) {
    const t = tiradaDeAbertura(muro, ab);
    if (!t) continue;
    if (!llegaAlPiso(ab)) cajas.push(cajaDeTirada(muro, t, 0, ab.antepecho_m));
    if (hayDintel(ab, muro.alto_m)) cajas.push(cajaDeTirada(muro, t, ab.antepecho_m + ab.alto_m, muro.alto_m));
  }

  return cajas;
}

// ---------------------------------------------------------------------------
// Cabriadas
// ---------------------------------------------------------------------------

/**
 * Las cabriadas de una nave: los reticulados que cruzan la luz corta, colgados
 * abajo del techo y repartidos parejo a lo largo del lado largo, sin apoyarse
 * en los bordes —donde ya hay muro—.
 *
 * `separacionM` es la separación buscada, no la exacta: se reparte parejo el
 * largo real para que no quede un vano suelto al final.
 */
export function cabriadas(nave: NaveMapa, separacionM = 5): Caja[] {
  const { x, y, ancho, alto } = nave.caja;
  // La cabriada salva la luz corta: si la nave es más ancha que larga, cruza a
  // lo largo y se repite a lo ancho.
  const aLoLargoDeY = alto >= ancho;
  const largo = aLoLargoDeY ? alto : ancho;
  if (separacionM <= 0 || largo <= separacionM) return [];

  const cantidad = Math.max(0, Math.round(largo / separacionM) - 1);
  const paso = largo / (cantidad + 1);
  const alturaDelCentro = nave.alto_m - ALTO_CABRIADA_M / 2;

  const cajas: Caja[] = [];
  for (let i = 1; i <= cantidad; i++) {
    const avance = paso * i;
    cajas.push(
      aLoLargoDeY
        ? {
            centro: [x + ancho / 2, alturaDelCentro, y + avance],
            tamano: [ancho, ALTO_CABRIADA_M, ESPESOR_CABRIADA_M],
            giroRad: 0,
          }
        : {
            centro: [x + avance, alturaDelCentro, y + alto / 2],
            tamano: [ESPESOR_CABRIADA_M, ALTO_CABRIADA_M, alto],
            giroRad: 0,
          },
    );
  }
  return cajas;
}

// ---------------------------------------------------------------------------
// Racks
// ---------------------------------------------------------------------------

/** Un pedazo de la estructura de un rack, ya con su papel: se pintan distinto. */
export type CajaDeRack = Caja & { tipo: 'parante' | 'larguero' };

/**
 * El rectángulo que ocupa el rack en el predio, sacado de las **posiciones**.
 *
 * No de `rack.geometria`, que sale cruda en coordenadas de la nave: sumarle el
 * rincón de la nave acá es el defecto más caro de la etapa —invisible en la
 * nave 1, que está en el origen, y visible en la 2, que arranca en x = 34—.
 * Derivar de las posiciones sale gratis otra ventaja: el backend ya achata la
 * rotación del rack en cubetas de 45° (`huellaRack`,
 * `backend/src/domain/planta.ts:130-137`), así que la huella heredada ya viene
 * con el giro resuelto y alineada a los ejes.
 *
 * Devuelve null si el rack está cargado pero **ninguna** de sus posiciones fue
 * ubicada (`api.ts:363`).
 */
export function huellaDeRack(rack: Rack): Huella | null {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of rack.posiciones) {
    if (!p.caja) continue;
    x0 = Math.min(x0, p.caja.x);
    y0 = Math.min(y0, p.caja.y);
    x1 = Math.max(x1, p.caja.x + p.caja.ancho);
    y1 = Math.max(y1, p.caja.y + p.caja.alto);
  }
  if (!Number.isFinite(x0)) return null;
  return { x: x0, y: y0, ancho: x1 - x0, alto: y1 - y0 };
}

/**
 * La estructura de un rack: los parantes verticales y los largueros
 * horizontales de sus **dos** bastidores, el de adelante y el de atrás.
 *
 * Son `(columnas + 1) × 2` parantes y `niveles × 2` largueros: un rack de 10
 * columnas y 4 niveles son 22 parantes y 8 largueros, no 11 y 8 —eso mezclaba
 * un bastidor con dos—. Los 15 racks del seed dan ≈356 mallas.
 *
 * La cantidad de columnas sale de las **posiciones con caja**, no de
 * `rack.columnas`: un rack a medio ubicar no tiene por qué inventar parantes
 * donde no hay nada.
 */
export function cajasDeRack(rack: Rack): CajaDeRack[] {
  const huella = huellaDeRack(rack);
  if (!huella) return [];

  const { niveles, altoNivelM } = rack.geometria;
  const altoTotal = niveles * altoNivelM;
  // El eje largo del rack: el backend lo deja siempre alineado a los ejes.
  const aLoLargoDeY = huella.alto > huella.ancho;
  const largo = aLoLargoDeY ? huella.alto : huella.ancho;
  const fondo = aLoLargoDeY ? huella.ancho : huella.alto;
  const desde = aLoLargoDeY ? huella.y : huella.x;
  const otro = aLoLargoDeY ? huella.x : huella.y;

  const columnas = rack.posiciones.filter((p) => p.caja !== null).length;
  const paso = columnas > 0 ? largo / columnas : largo;

  /** Una caja del rack, dada en coordenadas «a lo largo / a lo ancho» del rack. */
  const enElRack = (
    tipo: 'parante' | 'larguero',
    alLargo: number,
    alAncho: number,
    medidaLargo: number,
    medidaAncho: number,
    z0: number,
    z1: number,
  ): CajaDeRack => {
    const huellaLocal: Huella = aLoLargoDeY
      ? { x: alAncho, y: alLargo, ancho: medidaAncho, alto: medidaLargo }
      : { x: alLargo, y: alAncho, ancho: medidaLargo, alto: medidaAncho };
    return { ...cajaDesdeHuella(huellaLocal, z0, z1), tipo };
  };

  const cajas: CajaDeRack[] = [];
  // Los dos bastidores, cada uno pegado a su cara del rack.
  const bastidores = [otro, otro + fondo - ESPESOR_PARANTE_M];

  for (const base of bastidores) {
    // Un parante en cada borde de columna: n columnas son n + 1 parantes. El
    // último se corre para adentro para que no sobresalga del rack.
    for (let i = 0; i <= columnas; i++) {
      const avance = Math.min(desde + paso * i, desde + largo - ESPESOR_PARANTE_M);
      cajas.push(enElRack('parante', avance, base, ESPESOR_PARANTE_M, ESPESOR_PARANTE_M, 0, altoTotal));
    }
    // Un larguero por nivel, a la base de su nivel. Se lo apoya arriba del piso
    // en vez de centrarlo en cero, o el del nivel 1 queda medio enterrado en la
    // losa.
    for (let nivel = 1; nivel <= niveles; nivel++) {
      const z0 = (nivel - 1) * altoNivelM;
      cajas.push(enElRack('larguero', desde, base, largo, ESPESOR_LARGUERO_M, z0, z0 + ESPESOR_LARGUERO_M));
    }
  }
  return cajas;
}

// ---------------------------------------------------------------------------
// Huecos
// ---------------------------------------------------------------------------

/**
 * Un hueco de rack: una posición en un nivel, que es la unidad que el operario
 * toca en la pantalla.
 *
 * Lleva el `pallet` y el `articulo` del nivel porque son el tooltip de cada
 * hueco, y `hit` porque es lo que la búsqueda resalta —`/mapa?q=` no filtra
 * nada, sólo marca `posicion.hit` (`mapa.ts:366-378`), así que sin este campo
 * la búsqueda en 3D no resalta nada—.
 *
 * **El color no viaja acá**: `sector` es sólo un id, `Sector.color` es nullable
 * y el color real sale de `colorSector(color, índice en la lista de sectores)`.
 * Quien dibuje tiene que resolverlo con la lista completa de sectores a la
 * vista, como hace `Isometrico.tsx:384-388`.
 */
export type Hueco = {
  clave: string;
  codigo: string;
  sector: string;
  rack: string;
  nivel: number;
  ocupado: boolean;
  pallet: string | null;
  articulo: string | null;
  hit: boolean;
  caja: Caja;
};

/**
 * Los huecos de un sector, uno por posición y nivel.
 *
 * Dos cosas que se rompen fácil:
 *
 * - **Una posición sin `caja` se descarta**: es un rack cargado pero no ubicado
 *   en el plano (`api.ts:363`). Corolario: el conteo **no** es
 *   `columnas × niveles` en general.
 * - **Los niveles vienen en orden descendente** (`nivelesDe`,
 *   `backend/src/domain/planta.ts:104-105`: `[3, 2, 1]`). La altura se saca de
 *   `n.nivel`, nunca del índice del array, o la pila queda al revés y nadie lo
 *   nota hasta que el nivel 1 aparece arriba.
 */
export function huecosDeSector(sector: Sector): Hueco[] {
  const huecos: Hueco[] = [];
  for (const rack of sector.racks) {
    const alto = rack.geometria.altoNivelM;
    for (const p of rack.posiciones) {
      if (!p.caja) continue;
      for (const n of p.niveles) {
        const z0 = (n.nivel - 1) * alto;
        huecos.push({
          clave: `${p.codigo}-N${n.nivel}`,
          codigo: p.codigo,
          sector: sector.id,
          rack: rack.id,
          nivel: n.nivel,
          ocupado: n.ocupado,
          pallet: n.pallet,
          articulo: n.articulo,
          hit: p.hit,
          caja: cajaDesdeHuella(p.caja, z0, z0 + alto - LUZ_ENTRE_NIVELES_M),
        });
      }
    }
  }
  return huecos;
}

// ---------------------------------------------------------------------------
// Lo que hay adentro del hueco: la tarima y su carga
// ---------------------------------------------------------------------------

/**
 * Un hueco **no se dibuja como un bloque que lo llena**. Se dibuja como lo que
 * hay de verdad en un rack: una **tarima** apoyada sobre los largueros y, sólo
 * si el nivel está ocupado, una **caja de mercadería** encima. El hueco libre
 * queda con la tarima sola.
 *
 * Por qué importa, más allá del gusto: el bloque lleno **miente sobre el
 * volumen**. Un hueco de 1,74 m pintado macizo se lee como un pallet de 1,74 m
 * de carga, y arriba de todo tapaba la estructura del rack que está atrás. Con
 * la tarima y la caja, el aire que sobra en el nivel se ve, y el hueco libre se
 * distingue del ocupado **por la silueta** y no nada más por el color —que era
 * el único canal que tenía un `InstancedMesh` (ver el encabezado de
 * `Huecos3d.tsx`)—.
 *
 * La caja del hueco sigue existiendo igual, y sigue siendo la que manda: es la
 * que recibe el click, la que se contornea cuando la posición está elegida o la
 * búsqueda la marcó, y la que estas dos funciones subdividen. Nada de acá
 * cambia `huecosDeSector`.
 */

/** Alto de una tarima armada, de piso a plataforma. Es la medida estándar. */
export const ALTO_PALLET_M = 0.15;
/**
 * El aire que se le deja **arriba de la carga** dentro del hueco. Junto con
 * `ALTO_PALLET_M` es lo que hace que la caja no llegue al larguero de arriba,
 * que es lo que un pallet real tampoco hace.
 */
export const LUZ_SOBRE_LA_CARGA_M = 0.18;
/** Cuánto se mete la tarima para adentro del borde del hueco, por lado. */
const RETIRO_DEL_PALLET_M = 0.02;
/** Y cuánto más se mete la carga para adentro del borde de la tarima. */
const RETIRO_DE_LA_CARGA_M = 0.03;
/** Los tacos que se ven de frente en un pallet: tres, con dos entradas. */
const TACOS_POR_PALLET = 3;
/**
 * Cuántas cajas devuelve `cajasDePallet`: el patín de abajo, los tres tacos y
 * la plataforma de arriba. Está exportado porque quien instancia necesita el
 * total (`huecos × PIEZAS_POR_PALLET`) **antes** de armar las matrices.
 */
export const PIEZAS_POR_PALLET = 2 + TACOS_POR_PALLET;

/**
 * Un punto corrido respecto del centro de una caja, en **su** marco: `lx` y
 * `lz` se miden sobre los ejes de la caja y salen ya girados al mundo.
 *
 * Es el revés exacto de la cuenta que hace `dentroDeLaCaja` en `Huecos3d.tsx`
 * para verificar el índice del raycast, y tiene que seguir siéndolo. Hoy
 * `giroRad` es 0 en todos los huecos (`cajaDesdeHuella`), pero el contrato de
 * `Caja` no lo promete: sin esto, la tarima de un hueco girado quedaría
 * corrida hacia afuera de su propio hueco.
 */
function corrido(caja: Caja, lx: number, dy: number, lz: number): [number, number, number] {
  const cos = Math.cos(caja.giroRad);
  const sen = Math.sin(caja.giroRad);
  return [
    caja.centro[0] + cos * lx + sen * lz,
    caja.centro[1] + dy,
    caja.centro[2] - sen * lx + cos * lz,
  ];
}

/**
 * Las piezas de madera de la tarima que va apoyada en el fondo de un hueco:
 * el patín inferior, los tres tacos y la plataforma de arriba, en ese orden.
 *
 * Siempre `PIEZAS_POR_PALLET` cajas, o ninguna si el hueco es tan bajo que no
 * le entra ni la tarima —que no pasa con los límites de hoy (`altoNivelM`
 * mínimo 0,5 m contra 0,15 m de tarima) pero que no se puede dar por hecho
 * desde acá—.
 *
 * Los tacos se reparten sobre el lado **largo** de la huella, que es el que da
 * al pasillo: una posición del seed es 1,2 m de frente por 1,1 m de fondo
 * (`POR_DEFECTO`, `backend/src/domain/planta.ts`), así que las dos entradas de
 * la autoelevadora quedan mirando al pasillo, como en el depósito. Cuando la
 * huella es cuadrada da igual, y la regla sigue siendo determinista.
 */
export function cajasDePallet(hueco: Caja): Caja[] {
  const [tx, ty, tz] = hueco.tamano;
  const alto = Math.min(ALTO_PALLET_M, ty / 2);
  if (alto <= HOLGURA_M) return [];

  // El retiro no puede comerse la tarima entera: en un hueco angosto se
  // achica, no se invierte.
  const ancho = Math.max(tx - RETIRO_DEL_PALLET_M * 2, tx / 2);
  const fondo = Math.max(tz - RETIRO_DEL_PALLET_M * 2, tz / 2);
  const piso = -ty / 2;

  const plataforma = alto * 0.2;
  const patin = alto * 0.13;
  const taco = alto - plataforma - patin;

  const frenteEnX = ancho >= fondo;
  const alFrente = frenteEnX ? ancho : fondo;
  const lado = alFrente * 0.16;
  const paso = (alFrente - lado) / (TACOS_POR_PALLET - 1);

  const piezas: Caja[] = [
    {
      centro: corrido(hueco, 0, piso + patin / 2, 0),
      tamano: [ancho, patin, fondo],
      giroRad: hueco.giroRad,
    },
  ];
  for (let i = 0; i < TACOS_POR_PALLET; i += 1) {
    const d = -alFrente / 2 + lado / 2 + paso * i;
    piezas.push({
      centro: corrido(hueco, frenteEnX ? d : 0, piso + patin + taco / 2, frenteEnX ? 0 : d),
      tamano: frenteEnX ? [lado, taco, fondo] : [ancho, taco, lado],
      giroRad: hueco.giroRad,
    });
  }
  piezas.push({
    centro: corrido(hueco, 0, piso + alto - plataforma / 2, 0),
    tamano: [ancho, plataforma, fondo],
    giroRad: hueco.giroRad,
  });
  return piezas;
}

/**
 * La caja de mercadería que va **arriba** de la tarima, para un hueco ocupado.
 * `null` si el hueco no da para las dos cosas.
 *
 * Apoya en la plataforma de la tarima y deja `LUZ_SOBRE_LA_CARGA_M` de aire
 * arriba —acotado a un décimo del hueco, para que un nivel bajo no quede sin
 * carga—, y se mete un poco más para adentro que la tarima: ese escalón es lo
 * que deja ver el borde de madera abajo de la carga y lo que hace que la
 * silueta se lea como «pallet cargado» y no como un bloque.
 *
 * **Quién decide si va**: el que dibuja, mirando `Hueco.ocupado`. Acá entra
 * una `Caja` y no un `Hueco` a propósito — esta función es geometría, no
 * criterio.
 */
export function cajaDeCarga(hueco: Caja): Caja | null {
  const [tx, ty, tz] = hueco.tamano;
  const alto = Math.min(ALTO_PALLET_M, ty / 2);
  const luz = Math.min(LUZ_SOBRE_LA_CARGA_M, ty * 0.1);
  const altoCarga = ty - alto - luz;
  if (altoCarga <= HOLGURA_M) return null;

  const retiro = RETIRO_DEL_PALLET_M + RETIRO_DE_LA_CARGA_M;
  return {
    centro: [hueco.centro[0], hueco.centro[1] - ty / 2 + alto + altoCarga / 2, hueco.centro[2]],
    tamano: [
      Math.max(tx - retiro * 2, tx / 2),
      altoCarga,
      Math.max(tz - retiro * 2, tz / 2),
    ],
    giroRad: hueco.giroRad,
  };
}

// ---------------------------------------------------------------------------
// Qué muro entra al descarte por ángulo
// ---------------------------------------------------------------------------

/**
 * ¿Es un muro del perímetro de su nave? Se lo pregunta a la **geometría** —las
 * dos puntas caen sobre el mismo borde de la caja de la nave— y no al flag
 * `compartido`, que responde otra pregunta: si dos naves se reparten el mismo
 * plano. Es el mismo criterio, palabra por palabra, que usa el dibujo SVG
 * (`esPerimetral` en `components/Isometrico.tsx`), y por el mismo motivo: un
 * perimetral y una pared interior no se pueden dibujar igual.
 *
 * Las dos preguntas se combinan en `normalDeCulling`, acá abajo: al culling
 * entra el que es perimetral **y no** compartido. Un compartido es perimetral
 * —está sobre el borde de la caja— y aun así no se cullea.
 *
 * Sigue funcionando con los muros **recortados** que manda `/mapa` (los que se
 * reparten un plano con la nave de al lado, con clave `n2-oeste#1`): el
 * recorte es un pedazo del mismo borde, así que sigue cayendo sobre él.
 */
export function esPerimetral(m: MuroMapa, caja: Huella): boolean {
  const pegadoEnX = (x: number) => Math.abs(m.a.x - x) <= HOLGURA_M && Math.abs(m.b.x - x) <= HOLGURA_M;
  const pegadoEnY = (y: number) => Math.abs(m.a.y - y) <= HOLGURA_M && Math.abs(m.b.y - y) <= HOLGURA_M;
  return pegadoEnX(caja.x) || pegadoEnX(caja.x + caja.ancho) || pegadoEnY(caja.y) || pegadoEnY(caja.y + caja.alto);
}

/**
 * El normal exterior de un muro perimetral: la perpendicular a `a`-`b` en el
 * plano del piso, con el signo que la aleja del centro de la nave que lista el
 * muro (el punto `adentro` del SVG).
 *
 * Devuelve `null` cuando el muro es degenerado (las dos puntas en el mismo
 * lugar) o cuando su centro coincide con el de la nave: sin dirección no hay
 * signo, y un muro sin normal simplemente no se esconde nunca, que es el
 * comportamiento seguro.
 */
export function normalExterior(m: MuroMapa, caja: Huella): { x: number; z: number } | null {
  const dx = m.b.x - m.a.x;
  const dy = m.b.y - m.a.y;
  const largo = Math.hypot(dx, dy);
  if (largo <= HOLGURA_M) return null;
  // Perpendicular en el plano, normalizada. El eje `y` del plano es el `z` de
  // Three (ver la convención de ejes en `geometria.ts`).
  const px = -dy / largo;
  const pz = dx / largo;
  // Del centro de la nave al centro del muro: el sentido de "hacia afuera".
  const haciaAfueraX = (m.a.x + m.b.x) / 2 - (caja.x + caja.ancho / 2);
  const haciaAfueraZ = (m.a.y + m.b.y) / 2 - (caja.y + caja.alto / 2);
  const signo = px * haciaAfueraX + pz * haciaAfueraZ;
  if (Math.abs(signo) <= HOLGURA_M) return null;
  return signo > 0 ? { x: px, z: pz } : { x: -px, z: -pz };
}

/**
 * El normal con el que `Nave3d` cullea un muro, o `null` si ese muro **no se
 * cullea nunca**. Es la regla entera, en un solo lugar y probada.
 *
 * Devuelve `null` en tres casos, y los tres importan:
 *
 * 1. **La pared interior** (la oficina del seed): no separa adentro de afuera,
 *    y es una caja maciza que se lee bien de los dos lados.
 * 2. **El muro `compartido`**, que es el arreglo del 2026-09-07: es perímetro
 *    de la nave que lo lista pero **interior del predio**, porque `/mapa` emite
 *    el plano que dos naves pegadas comparten una sola vez y se lo queda la de
 *    `naveId` más bajo (`resolverMuros`). Culleado, su normal apunta hacia la
 *    nave de al lado, así que **desde esa nave el muro desaparecía** y esa nave
 *    se veía sin pared —con el interior de la dueña a la vista por un plano que
 *    sí existe—. El encabezado de `Nave3d.tsx` lo cuenta entero.
 * 3. **El muro degenerado**, con las dos puntas en el mismo lugar o con el
 *    centro justo sobre el centro de la nave: no hay perpendicular ni hay
 *    "afuera" que calcular.
 */
export function normalDeCulling(m: MuroMapa, caja: Huella): { x: number; z: number } | null {
  if (m.compartido) return null;
  if (!esPerimetral(m, caja)) return null;
  return normalExterior(m, caja);
}

// ---------------------------------------------------------------------------
// Cámara
// ---------------------------------------------------------------------------

/**
 * Cuánto abre la cámara de la escena, en grados y en vertical. Es el `fov` que
 * se le pasa al `<Canvas camera>` y **el mismo número tiene que usar el
 * encuadre**: si el encuadre calcula la distancia con 45° y la cámara dibuja
 * con 50°, el predio entra con una banda de aire al costado o se sale de
 * cuadro. Vive acá, con la cuenta que lo consume.
 */
export const FOV_GRADOS = 45;

/**
 * El aire que se le deja alrededor a lo que se encuadra. 1 sería la caja
 * tocando los cuatro bordes del lienzo, que se lee como un dibujo apretado.
 */
export const MARGEN_ENCUADRE = 1.06;

/** Desde qué ángulo y con qué lienzo se mira. Ver `encuadrar`. */
export type Vista = {
  /**
   * Giro alrededor del depósito, en grados, **con la misma convención que el
   * dibujo SVG** (`hacerCamara` de `lib/camara3d.ts`): 0 pone el ojo sobre el
   * eje `y` del plano —el sur, porque `y` crece hacia el sur— y el giro avanza
   * hacia el este. Así los dos motores leen el mismo número en el mismo dial.
   */
  giroGrados: number;
  /** Altura del punto de vista: 0 es el alzado (de frente), 90 la planta pura. */
  alturaGrados: number;
  /** Ancho / alto del lienzo. Sin esto el encuadre deja bandas vacías al costado. */
  aspecto: number;
  fovGrados?: number;
  margen?: number;
};

/**
 * Hacia dónde está el ojo, visto desde lo que se mira: el vector **unitario**
 * que va del objetivo a la cámara, en los tres ejes de Three.
 *
 * Es `ojo` de `hacerCamara` (`lib/camara3d.ts`) pasado a tres dimensiones —el
 * `y` del plano es el `z` de acá— y **es la única traducción del dial a una
 * dirección en toda la escena**: la usan el encuadre, el giro de los
 * deslizadores y la vuelta del `OrbitControls`. Escrita dos veces, los dos
 * motores leerían distinto el mismo número del dial.
 */
export function direccionDeVista(giroGrados: number, alturaGrados: number): [number, number, number] {
  const g = (giroGrados * Math.PI) / 180;
  const a = (alturaGrados * Math.PI) / 180;
  return [Math.sin(g) * Math.cos(a), Math.sin(a), Math.cos(g) * Math.cos(a)];
}

/**
 * El revés de `direccionDeVista`: qué marca el dial para una cámara que ya
 * está puesta. Es lo que permite que arrastrar con la ruedita mueva los
 * deslizadores, en vez de dejarlos mintiendo el ángulo con el que arrancó.
 *
 * El giro sale normalizado a [0, 360), que es el rango del deslizador. Mirando
 * derecho para abajo el giro no está definido —cualquiera da la misma vista— y
 * se devuelve 0.
 */
export function anguloDeVista(x: number, y: number, z: number): { giroGrados: number; alturaGrados: number } {
  const largo = Math.hypot(x, y, z);
  if (largo <= 1e-9) return { giroGrados: 0, alturaGrados: 0 };
  const alturaGrados = (Math.asin(Math.min(1, Math.max(-1, y / largo))) * 180) / Math.PI;
  const enPlanta = Math.hypot(x, z);
  if (enPlanta <= 1e-9) return { giroGrados: 0, alturaGrados };
  const crudo = (Math.atan2(x, z) * 180) / Math.PI;
  return { giroGrados: ((crudo % 360) + 360) % 360, alturaGrados };
}

/**
 * Dónde poner la cámara para que una caja del plano —el predio entero, o la
 * huella de un rack— **entre completa** en el lienzo, mirándola desde el ángulo
 * que se pida.
 *
 * Es la cuenta que en el SVG hacían `encuadrarCaja` + `segunLaPantalla`
 * (`lib/camara3d.ts`), y hay que hacerla igual de en serio: una distancia
 * inventada a ojo —`diagonal × 0.9`, digamos— alcanza para una pantalla y
 * recorta el predio en la otra, porque lo que entra depende del `fov` **y del
 * aspecto del lienzo**.
 *
 * Cómo: se arma la base de la cámara (derecha, arriba, y la dirección hacia el
 * ojo), se proyectan los **ocho vértices** de la caja sobre esa base, y para
 * cada uno se despeja a qué distancia deja de salirse del cono de visión —a lo
 * ancho y a lo alto—. Se toma la mayor. Con la distancia resuelta, la posición
 * es el objetivo más esa distancia en la dirección del ojo.
 *
 * El objetivo es el **centro** de la caja, altura incluida: mirando al piso, un
 * galpón de 8 m se va para arriba del cuadro.
 */
export function encuadrar(
  huella: Huella,
  altoM: number,
  vista: Vista,
): { posicion: [number, number, number]; objetivo: [number, number, number]; distancia: number } {
  const { giroGrados, alturaGrados, aspecto } = vista;
  const fov = ((vista.fovGrados ?? FOV_GRADOS) * Math.PI) / 180;
  const margen = vista.margen ?? MARGEN_ENCUADRE;

  const d = direccionDeVista(giroGrados, alturaGrados);

  const objetivo: [number, number, number] = [
    huella.x + huella.ancho / 2,
    altoM / 2,
    huella.y + huella.alto / 2,
  ];

  // La derecha de la cámara: horizontal siempre, perpendicular a la dirección
  // del ojo. Mirando derecho para abajo no hay una sola respuesta —cualquier
  // horizontal sirve— y se elige una para no dividir por cero.
  const enPlanta = Math.hypot(d[0], d[2]);
  const derecha: [number, number, number] =
    enPlanta > 1e-6 ? [d[2] / enPlanta, 0, -d[0] / enPlanta] : [1, 0, 0];
  // Y el arriba de la cámara sale del producto vectorial de las otras dos.
  const arriba: [number, number, number] = [
    d[1] * derecha[2] - d[2] * derecha[1],
    d[2] * derecha[0] - d[0] * derecha[2],
    d[0] * derecha[1] - d[1] * derecha[0],
  ];

  const tanAlto = Math.tan(fov / 2);
  const tanAncho = tanAlto * Math.max(aspecto, 0.01);

  let distancia = 0;
  for (const x of [huella.x, huella.x + huella.ancho]) {
    for (const z of [huella.y, huella.y + huella.alto]) {
      for (const y of [0, altoM]) {
        const v: [number, number, number] = [x - objetivo[0], y - objetivo[1], z - objetivo[2]];
        const hacia = v[0] * d[0] + v[1] * d[1] + v[2] * d[2];
        const alAncho = Math.abs(v[0] * derecha[0] + v[1] * derecha[1] + v[2] * derecha[2]) * margen;
        const alAlto = Math.abs(v[0] * arriba[0] + v[1] * arriba[1] + v[2] * arriba[2]) * margen;
        // A `distancia` del objetivo, el vértice queda a `distancia - hacia` de
        // la cámara, y ahí el cono deja ver `(distancia - hacia) × tan`.
        distancia = Math.max(distancia, hacia + alAncho / tanAncho, hacia + alAlto / tanAlto);
      }
    }
  }
  // Nunca menos de un metro: una caja degenerada (un rack sin ubicar) dejaría
  // la cámara adentro del objetivo y la escena se vería del revés.
  distancia = Math.max(distancia, 1);

  return {
    posicion: [
      objetivo[0] + d[0] * distancia,
      objetivo[1] + d[1] * distancia,
      objetivo[2] + d[2] * distancia,
    ],
    objetivo,
    distancia,
  };
}

/**
 * El encuadre de arranque: el predio entero desde el ángulo isométrico de
 * siempre, que es el encuadre con el que se entiende la forma del edificio.
 *
 * **Cambió de firma en la tarea 8**, y a propósito: la versión de la tarea 2
 * ponía la cámara a `diagonal × 0,9` del centro sin mirar el `fov` ni el
 * lienzo, y con `fov` 45 eso **recorta el predio** —a 65 m de un predio de
 * 60 × 40 el cono deja ver 27 m de radio y los vértices están a 36—. No tenía
 * consumidor todavía, así que el arreglo no rompe nada; la cuenta de verdad es
 * `encuadrar`.
 */
export function encuadreInicial(
  predio: Mapa['predio'],
  altoM: number,
  vista: Pick<Vista, 'giroGrados' | 'alturaGrados' | 'aspecto'>,
) {
  return encuadrar({ x: 0, y: 0, ancho: predio.ancho, alto: predio.largo }, altoM, vista);
}

/**
 * La caja de aire de una abertura: el hueco que `cajasDeMuro` le descontó al
 * muro, con el espesor del muro y entre el antepecho y el dintel.
 *
 * **No se dibuja**: en 3D una abertura es el aire, y ponerle una caja de color
 * la volvería a tapar (ver el encabezado de `Nave3d.tsx`). Existe para poder
 * montar ahí una malla **invisible** que reciba el puntero, y así el portón y
 * el ventanal tengan su globito con nombre, tipo y alto como lo tienen en el
 * dibujo SVG (`Isometrico.tsx:214`) — que en 3D es el **único** lugar donde
 * queda ese dato, porque acá el tipo de abertura ya no se distingue por color.
 *
 * Devuelve `null` si la abertura no le pisa nada al muro (una abertura que el
 * backend recortó a cero), que es la misma guarda que usa `cajasDeMuro`.
 */
export function cajaDeAbertura(muro: MuroMapa, ab: AberturaMapa): Caja | null {
  const t = tiradaDeAbertura(muro, ab);
  if (!t) return null;
  return cajaDeTirada(muro, t, ab.antepecho_m, ab.antepecho_m + ab.alto_m);
}
